import { useState } from 'react';

import { Modal } from 'cpool-components/general/Modal';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { MAX_FILE_SIZE, MAX_UPLOAD_FILE_COUNT } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuCircleCheck, LuCircleX } from 'react-icons/lu';
import { toast } from 'react-toastify';

import { Dropzone } from './Dropzone';
import type { RemoveUploadedHandler, UploadedItem, UploadFileHandler, UploadingItem } from './types';
import { UPLOAD_ITEM_STATUS } from './types';
import { UploadedFileRow } from './UploadedFileRow';
import { UploadProgressRow } from './UploadProgressRow';
import { useUploadQueue } from './useUploadQueue';

export type UploadModalProps = {
    open: boolean;
    onClose: () => void;
    accept?: string[] | null;
    size?: number;
    disabled?: boolean;
    multiple?: boolean;
    uploadFile: UploadFileHandler;
    removeUploaded: RemoveUploadedHandler;
};

// Characters that must not appear in a file name (path separators and reserved characters).
const INVALID_FILE_NAME_CHARS = /[<>:"/\\|?*]/;

const UploadModal: React.FC<UploadModalProps> = (props) => {
    const { accept, disabled, multiple, onClose, open, removeUploaded, size = MAX_FILE_SIZE, uploadFile } = props;
    const { t } = useTranslation();
    const [errors, setErrors] = useState<string[]>([]);
    const [rollingBack, setRollingBack] = useState(false);

    const queue = useUploadQueue(uploadFile, removeUploaded, (message, fileName) => {
        const entry = t('FILE_UPLOAD.FILE_ERROR', { file: fileName, message });
        setErrors((prev) => (prev.includes(entry) ? prev : [...prev, entry]));
    });

    const uploadingItems = queue.items.filter(
        (item): item is UploadingItem => item.status === UPLOAD_ITEM_STATUS.UPLOADING,
    );
    const uploadedItems = queue.items.filter(
        (item): item is UploadedItem => item.status === UPLOAD_ITEM_STATUS.SUCCESS,
    );

    const handleSelect = (files: File[] | File | null) => {
        // A rejected selection is reported through the dropzone's onError, which sets `errors`.
        if (files === null) return;
        const list = Array.isArray(files) ? files : [files];

        if (list.some((file) => INVALID_FILE_NAME_CHARS.test(file.name))) {
            setErrors([t('FILE_UPLOAD.INVALID_FILE_NAME')]);
            return;
        }

        // FilePicker already validated type and size; the count cap is enforced here across the session.
        const existingCount = queue.items.length;
        if (existingCount + list.length > MAX_UPLOAD_FILE_COUNT) {
            setErrors([t('FILE_UPLOAD.TOO_MANY_FILES', { count: MAX_UPLOAD_FILE_COUNT })]);
            return;
        }

        setErrors([]);
        queue.add(list);
    };

    const closeAndReset = () => {
        queue.reset();
        setErrors([]);
        onClose();
    };

    const isBusy = queue.isUploading || rollingBack;

    const handleDismiss = () => {
        if (isBusy) return;
        closeAndReset();
    };

    // Cancel rolls back this session's uploads; dismiss/"Fertig" keep them and are blocked while uploading
    const handleCancel = async () => {
        uploadingItems.filter((item) => item.progress < 100).forEach((item) => queue.cancel(item.id));
        setRollingBack(true);
        const failed = await queue.rollback();
        setRollingBack(false);
        if (failed.length > 0) {
            const names = failed.map((item) => item.file.name);
            toast.error(t('FILE_UPLOAD.ROLLBACK_FAILED', { files: names.join(', ') }));
            return;
        }
        closeAndReset();
    };

    const handleDone = () => {
        if (isBusy) return;
        closeAndReset();
    };

    const hasFinishedFiles = uploadedItems.length > 0;

    return (
        <Modal
            open={open}
            onClose={handleDismiss}
            variant="sidebar"
            side="right"
            width="480px"
            title={t('FILE_UPLOAD.MODAL_TITLE')}
        >
            <div className="flex flex-col gap-4 p-4">
                <p className="text-sm text-midnight">{t('FILE_UPLOAD.ADD_DOCUMENTS')}</p>

                <Dropzone
                    accept={accept}
                    disabled={disabled}
                    errors={errors}
                    multiple={multiple}
                    onError={(message) => setErrors([message])}
                    onSelect={handleSelect}
                    size={size}
                />

                {uploadingItems.map((item) => (
                    <UploadProgressRow
                        key={item.id}
                        name={item.file.name}
                        progress={item.progress}
                        onCancel={() => queue.cancel(item.id)}
                    />
                ))}

                {hasFinishedFiles && (
                    <div className="flex flex-col gap-2">
                        <p className="text-sm font-medium text-midnight">{t('FILE_UPLOAD.UPLOADED_TITLE')}</p>
                        {uploadedItems.map((item) => (
                            <UploadedFileRow
                                key={item.id}
                                name={item.file.name}
                                size={item.file.size}
                                removing={item.removing}
                                onRemove={() => queue.remove(item)}
                            />
                        ))}
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        iconLeft={<LuCircleX className="h-4 w-4" />}
                        disabled={rollingBack}
                        onClick={handleCancel}
                    >
                        {t('BUTTONS.CANCEL')}
                    </Button>
                    <Button
                        iconLeft={<LuCircleCheck className="h-4 w-4" />}
                        disabled={disabled || isBusy}
                        onClick={handleDone}
                    >
                        {t('BUTTONS.FINISHED')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default UploadModal;
